import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  DollarSign, Sparkles, Rocket, GraduationCap, Scale, ArrowRight,
  ChevronDown, TrendingUp, Compass, LineChart, ShieldCheck, Mail, Youtube, HelpCircle, GitCompareArrows,
} from "lucide-react";
import usePageSeo from "@/hooks/usePageSeo";
import ContactDialog from "@/components/ContactDialog";
import DownloadPill from "@/components/DownloadPill";
import { PricingScorecardBody } from "@/components/pricing/PricingScorecard";

// Q&A payload — kept local so this page is self-contained.
// Each answer is written for two audiences: a beginner who wants a plain-English
// answer, and a curious visitor who wants to know exactly HOW DPP delivers it.
const FAQS = [
  {
    id: "income-needed",
    icon: DollarSign,
    tag: "Income target",
    q: "How much do I need to invest to earn $1,000+ a month in dividends?",
    lede:
      "It depends on the yield of your portfolio — but DPP can reverse-solve it for you in seconds.",
    body: [
      "A rough back-of-the-envelope: to earn $1,000/month ($12,000/yr), you'd need about $400,000 at a 3% yield, $300,000 at 4%, or $240,000 at 5%. Real portfolios grow their dividends over time, so the number gets smaller if you have runway.",
      "Instead of guessing, open the **AI Income Target Planner** inside DPP: type $1,000 (or any target), pick a portfolio, choose whether you plan to invest a lump sum or contribute monthly, and DPP reverse-solves three paths for you — lump-sum today at current yield-on-cost, lump-sum with expected dividend growth, and monthly DCA + years-to-target. Claude Sonnet 4.6 writes a plain-English narrative explaining the assumptions and risks.",
      "The math is anchored on **real historical yield-on-cost, CAGR, and dividend-growth streaks** from the tickers you actually pick — not stock-tip averages.",
    ],
    cta: { label: "Try the Income Target Planner", to: "/portfolio/new" },
  },
  {
    id: "why-dpp",
    icon: Sparkles,
    tag: "Why DPP",
    q: "Why should I use Dividend Portfolio Planner?",
    lede:
      "Because \"what would this portfolio have paid me over the last 20 years — with DRIP on\" should take 10 seconds, not a spreadsheet weekend.",
    body: [
      "DPP is a **planner**, not a broker. It exists to answer the questions you can't answer inside your brokerage app: what income would this basket have thrown off? How much would it compound with dividend reinvestment? What weights would maximise yield-on-cost without wrecking growth?",
      "**What you get, free:** unlimited backtests, DRIP toggle, decades of price + dividend history via Yahoo Finance, side-by-side scenario comparison, and a live **Track My Holdings** view for the real positions in your brokerage — with ex-dividend calendar and forward income projections.",
      "**What Pro adds:** unlimited AI Optimizer, unlimited Income Target Planner solves, AI Research Chat on any ticker, and priority backtest queue.",
      "Every metric on screen has an **AI Explain** button that answers \"why did this happen?\" in plain English. No jargon walls, no advisor fees.",
    ],
    cta: { label: "See the full feature list", to: "/pricing" },
  },
  {
    id: "live-off-dividends",
    icon: Rocket,
    tag: "FIRE math",
    q: "Can I live off dividends without waiting until retirement?",
    lede:
      "Yes — but only if the math is honest. DPP shows you exactly which year the numbers cross the line.",
    body: [
      "Living off dividends before 65 requires three things: enough capital, a yield you can sustain **through** market downturns, and dividend growers that outpace inflation. DPP tests all three at once.",
      "Point the **Income Target Planner** at your monthly expenses (e.g. $4,500/month), set a realistic DCA contribution (say $2,000/month), and let it solve for the **year your portfolio's forward monthly income crosses your target**. The projection chart shows the exact month with a milestone strip — you can see FIRE arrive.",
      "Then stress-test it: turn DRIP off, drop the assumed dividend growth to a bearish rate, or swap a covered-call ETF in for a dividend grower. Backtests re-run in seconds so you can pressure-test the plan the way a good financial planner would.",
      "**Reality check baked in:** the AI narrative flags survivorship bias, tax drag on non-qualified dividends, and sequence-of-returns risk. You'll know the assumptions before you commit real capital.",
    ],
    cta: { label: "Backtest a FIRE plan", to: "/portfolio/new" },
  },
  {
    id: "best-for-beginners",
    icon: GraduationCap,
    tag: "For beginners",
    q: "What's the best dividend planning app for beginners?",
    lede:
      "The one that explains its own numbers. That's the design promise of DPP.",
    body: [
      "Most dividend tools throw you into a spreadsheet of yield %, payout ratio, and CAGR without ever explaining what those numbers mean or which one matters. DPP inverts the flow: every metric has a hover-tip and a full **Investment 101** page with a floating glossary and an interactive DRIP calculator you can play with while you read.",
      "You get an **anonymous builder** — no signup wall — where you can drop tickers like SCHD, VYM, or JEPI, adjust weights with sliders, and see live 20-year backtests. The AI Explain card writes a beginner-friendly narrative under every result.",
      "Guardrails are built in: the **Dividend Safety Score** on the Research tab warns you when a stock has a payout ratio above 100% or a broken growth streak. You won't chase a yield trap by accident.",
    ],
    cta: { label: "Open Investment 101", to: "/how-it-works" },
  },
  {
    id: "growth-vs-yield",
    icon: Scale,
    tag: "Investing 101",
    q: "What's the difference between dividend growth and high-yield stocks?",
    lede:
      "One pays you a little today and a lot more later. The other pays you a lot today and probably won't grow.",
    body: [
      "**Dividend growth stocks** (think MSFT, JNJ, KO, SCHD) pay a modest yield today — often 1.5% to 3% — but raise the dividend every year, sometimes for 25+ years running. Over decades, your **yield-on-cost** (dividend / what you originally paid) can climb to 8% or more without you doing anything.",
      "**High-yield stocks** (many REITs, BDCs, covered-call ETFs like JEPI or QYLD) pay 6-12% today but typically don't grow the dividend — and some cut it during recessions. The upfront income is real, but 20 years later your yield-on-cost may still be sitting where it started.",
      "DPP lets you **compare them side by side**. Build a Growth portfolio (SCHD-heavy), build a Yield portfolio (JEPI-heavy), open **Compare**, and see two 20-year backtests with combined dividend and value charts. You'll see exactly which one hits $1,000/month first — and which one grows past it fastest.",
      "The **AI Research Chat** on the Research tab will explain the trade-offs for the specific tickers you're weighing, using real payout ratios, growth streaks, and safety scores.",
    ],
    cta: { label: "Compare two portfolios", to: "/compare" },
  },
];

export default function WhyDpp() {
  const [contactOpen, setContactOpen] = useState(false);
  const [compareOpen, setCompareOpen] = useState(false);

  // SEO: title, meta description, canonical, and FAQPage JSON-LD.
  usePageSeo({
    title: "How We Rank — Dividend Portfolio Planner FAQ",
    description:
      "Plain-English answers to top dividend questions: how much to invest for $1,000/mo, when DRIP wins, how we rank safety.",
    path: "/how-we-rank",
    ogSlug: "how-we-rank",
    jsonLdId: "how-we-rank-faq-jsonld",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQS.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: {
          "@type": "Answer",
          text: [f.lede, ...f.body.map((p) => p.replace(/\*\*(.+?)\*\*/g, "$1"))].join(" "),
        },
      })),
    },
  });

  return (
    <article className="max-w-4xl mx-auto px-6 py-16" aria-labelledby="why-dpp-heading">
      {/* Header */}
      <header className="text-center max-w-2xl mx-auto">
        <div className="mono text-[11px] uppercase tracking-[0.25em] text-secondary">How We Rank</div>
        <h1 id="why-dpp-heading" className="serif text-5xl sm:text-6xl mt-3">
          The dividend questions{" "}
          <span className="italic text-primary">everyone actually asks</span>.
        </h1>
        <p className="text-muted-foreground mt-4 text-base">
          Plain-English answers to the five questions we hear the most — and how DPP answers each one with real historical data, not vibes.
        </p>
      </header>

      {/* Quick jump chips */}
      <nav aria-label="Jump to FAQ topic" className="mt-8 flex flex-wrap justify-center gap-2" data-testid="why-dpp-jump-chips">
        {FAQS.map((f) => (
          <a
            key={f.id}
            href={`#${f.id}`}
            className="mono text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full border border-border text-muted-foreground hover:border-primary/40 hover:text-primary transition-colors"
            data-testid={`why-jump-${f.id}`}
          >
            {f.tag}
          </a>
        ))}
      </nav>

      {/* Primary contact CTA — sits between the topic chips and the first
          FAQ so visitors with a specific question can reach us before
          committing to reading five answers. */}
      <div className="mt-6 flex justify-center gap-2 flex-wrap" data-testid="why-contact-top-wrap">
        <Button
          variant="outline"
          onClick={() => setCompareOpen(true)}
          className="rounded-full"
          data-testid="why-compare-top"
        >
          <GitCompareArrows className="w-4 h-4 mr-2" /> Compare vs competitors
        </Button>
        <Button
          variant="outline"
          onClick={() => setContactOpen(true)}
          className="rounded-full"
          data-testid="why-contact-top"
        >
          <Mail className="w-4 h-4 mr-2" /> Contact us
        </Button>
        <a
          href="https://www.youtube.com/@DivPortplan"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center rounded-full border border-primary/30 text-primary hover:bg-primary/10 transition-colors px-4 h-9 text-sm"
          data-testid="why-youtube-top"
        >
          <Youtube className="w-4 h-4 mr-2" /> YouTube channel
        </a>
        <Link
          to="/faq"
          className="inline-flex items-center rounded-full border border-primary/30 text-primary hover:bg-primary/10 transition-colors px-4 h-9 text-sm"
          data-testid="why-faq-top"
        >
          <HelpCircle className="w-4 h-4 mr-2" /> FAQ
        </Link>
      </div>

      {/* FAQ cards */}
      <section aria-labelledby="faq-heading" className="mt-12 space-y-5" data-testid="why-dpp-faq-list">
        <h2 id="faq-heading" className="sr-only">Frequently Asked Questions</h2>
        {FAQS.map((f, i) => (
          <FaqCard key={f.id} faq={f} index={i} />
        ))}
      </section>

      {/* Closing CTA band */}
      <div className="mt-16 rounded-2xl border border-primary/20 bg-primary/5 p-8 text-center" data-testid="why-dpp-cta-band">
        <div className="mono text-[10px] uppercase tracking-[0.25em] text-secondary">Ready to see the math on your own portfolio?</div>
        <h2 className="serif text-3xl sm:text-4xl mt-3 text-primary">Stop guessing. Start planning.</h2>
        <p className="text-muted-foreground mt-3 max-w-lg mx-auto text-sm">
          Build a portfolio in 60 seconds and run a 20-year backtest — no signup required.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <Link to="/portfolio/new" data-testid="why-cta-try">
            <Button className="rounded-full">
              Try DPP for free <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
          <Link to="/how-it-works" data-testid="why-cta-learn">
            <Button variant="outline" className="rounded-full">
              <GraduationCap className="w-4 h-4 mr-2" /> Investment 101
            </Button>
          </Link>
          <DownloadPill variant="outline" testId="why-cta-download" label="Download DPP" />
        </div>
      </div>

      {/* Trust bar */}
      <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
        <TrustCell icon={LineChart} label="20+ yrs history" sub="Yahoo Finance data" />
        <TrustCell icon={Sparkles} label="AI Explain" sub="Every metric, plain English" />
        <TrustCell icon={Compass} label="Research chat" sub="Ask any ticker" />
        <TrustCell icon={ShieldCheck} label="Safety scores" sub="Payout ratio + streak" />
      </div>

      <p className="mt-10 text-center text-[11px] mono text-muted-foreground uppercase tracking-widest">
        Educational tool · Not investment advice
      </p>

      <ContactDialog
        open={contactOpen}
        onOpenChange={setContactOpen}
      />

      {/* Feature comparison dialog — full scorecard rendered ON this page.
          Nothing navigates away; the user stays on Why DPP. */}
      <Dialog open={compareOpen} onOpenChange={setCompareOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto p-0" data-testid="why-compare-dialog">
          <DialogHeader className="px-6 pt-6">
            <DialogTitle className="serif text-3xl inline-flex items-center gap-2">
              <GitCompareArrows className="w-5 h-5 text-primary" />
              Feature comparison
            </DialogTitle>
            <DialogDescription>
              Side-by-side of DPP against Snowball Analytics, Sharesight, and Seeking Alpha — real feature rows across pricing, holdings, dividends, analysis, and workflow. Checked against public pricing pages Q1 2026.
            </DialogDescription>
          </DialogHeader>
          <div className="px-2 pb-4">
            <PricingScorecardBody />
          </div>
        </DialogContent>
      </Dialog>
    </article>
  );
}

function FaqCard({ faq, index }) {
  const [open, setOpen] = useState(index === 0);
  const Icon = faq.icon;
  return (
    <article aria-labelledby={`${faq.id}-heading`}>
      <Card
        id={faq.id}
        className="scroll-mt-24 border border-border card-hover"
        data-testid={`why-faq-${faq.id}`}
      >
        <CardContent className="p-6 sm:p-7">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="w-full flex items-start gap-4 text-left"
          aria-expanded={open}
          data-testid={`why-faq-toggle-${faq.id}`}
        >
          <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary grid place-items-center shrink-0">
            <Icon className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <Badge variant="outline" className="mono text-[10px] uppercase tracking-widest border-secondary/40 text-secondary mb-2">
              {faq.tag}
            </Badge>
            <h3 id={`${faq.id}-heading`} className="serif text-2xl sm:text-3xl leading-tight text-foreground">
              {faq.q}
            </h3>
            <p className="text-muted-foreground mt-2 text-sm">
              {faq.lede}
            </p>
          </div>
          <ChevronDown
            className={`w-5 h-5 text-muted-foreground shrink-0 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
          />
        </button>

        {open && (
          <div className="mt-5 pl-0 sm:pl-14 space-y-3" data-testid={`why-faq-body-${faq.id}`}>
            {faq.body.map((p, i) => (
              <p
                key={i}
                className="text-[15px] leading-relaxed text-foreground/85"
                // Simple **bold** parser — no external markdown dependency needed.
                dangerouslySetInnerHTML={{
                  __html: p.replace(
                    /\*\*(.+?)\*\*/g,
                    '<strong class="text-primary font-semibold">$1</strong>',
                  ),
                }}
              />
            ))}
            {faq.cta && (
              <div className="pt-2">
                <Link to={faq.cta.to} data-testid={`why-faq-cta-${faq.id}`}>
                  <Button variant="outline" className="rounded-full">
                    {faq.cta.label} <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
    </article>
  );
}

function TrustCell({ icon: Icon, label, sub }) {
  return (
    <div className="rounded-lg border border-border bg-card px-3 py-3 flex flex-col items-center gap-1">
      <Icon className="w-4 h-4 text-primary" />
      <div className="mono text-[10px] uppercase tracking-widest text-foreground">{label}</div>
      <div className="text-[10px] text-muted-foreground">{sub}</div>
    </div>
  );
}
